// Home Insurance Files Storage
// Temporary in-memory storage for document files during the policy flow
// Files are stored here because Redux cannot serialize File/Blob objects

// Storage map for home insurance documents
const homeFiles = new Map();

// File keys for home insurance documents
export const HOME_FILE_KEYS = {
  CPR_FRONT: 'home_cpr_front',
  CPR_BACK: 'home_cpr_back',
};

/**
 * Store a file for home insurance policy
 * @param {string} key - File key (use HOME_FILE_KEYS)
 * @param {File} file - The file to store
 */
export const storeHomeFile = (key, file) => {
  if (file instanceof File || file instanceof Blob) {
    homeFiles.set(key, file);
    console.log('[Home Storage] Stored file:', key, file.name || 'blob');
  }
};

/**
 * Get a stored file
 * @param {string} key - File key
 * @returns {File|null} The stored file or null
 */
export const getHomeFile = (key) => {
  return homeFiles.get(key) || null;
};

/**
 * Get all stored home files
 * @returns {Object} Object with all stored files { cprFront, cprBack }
 */
export const getAllHomeFiles = () => {
  return {
    cprFront: homeFiles.get(HOME_FILE_KEYS.CPR_FRONT) || null,
    cprBack: homeFiles.get(HOME_FILE_KEYS.CPR_BACK) || null,
  };
};

/**
 * Get all files as an array for Vista submission
 * @returns {File[]} Array of files in order [cprFront, cprBack]
 */
export const getHomeFilesArray = () => {
  const files = [];
  const cprFront = homeFiles.get(HOME_FILE_KEYS.CPR_FRONT);
  const cprBack = homeFiles.get(HOME_FILE_KEYS.CPR_BACK);

  if (cprFront) files.push(cprFront);
  if (cprBack) files.push(cprBack);

  return files;
};

/**
 * Get document types array matching the files order
 * @returns {string[]} Array of document type labels
 */
export const getHomeDocumentTypes = () => {
  const types = [];
  if (homeFiles.has(HOME_FILE_KEYS.CPR_FRONT)) types.push('CPR (Front)');
  if (homeFiles.has(HOME_FILE_KEYS.CPR_BACK)) types.push('CPR (Back)');
  return types;
};

/**
 * Remove a specific file
 * @param {string} key - File key to remove
 */
export const removeHomeFile = (key) => {
  homeFiles.delete(key);
  console.log('[Home Storage] Removed file:', key);
};

/**
 * Clear all stored home files
 * Call this after successful policy creation or when user exits flow
 */
export const clearHomeFiles = () => {
  homeFiles.clear();
  console.log('[Home Storage] Cleared all files');
};

/**
 * Get count of stored files
 * @returns {number} Number of stored files
 */
export const getHomeFilesCount = () => {
  return homeFiles.size;
};

/**
 * Check if all required files are stored
 * @returns {boolean} True if both CPR files are present
 */
export const hasAllHomeFiles = () => {
  return (
    homeFiles.has(HOME_FILE_KEYS.CPR_FRONT) &&
    homeFiles.has(HOME_FILE_KEYS.CPR_BACK)
  );
};

export default {
  storeHomeFile,
  getHomeFile,
  getAllHomeFiles,
  getHomeFilesArray,
  getHomeDocumentTypes,
  removeHomeFile,
  clearHomeFiles,
  getHomeFilesCount,
  hasAllHomeFiles,
  HOME_FILE_KEYS,
};
